import React, {Component} from 'react';
import css from './SearchList.module.scss';
import onClickOutside from 'react-onclickoutside';


/**
 * @class
 *
 * A search input component with the results listed in a dropdown.
 *
 * @prop placeholder {string}: The placeholder text for the input field of the component.
 * @prop displayedProperty {string}: Since the component can accept a list of objects with variable properties,
 * we need to specify the name of the property used for displaying text/label in the dropdown.
 * @prop filterBy {string | array}: String or an array of strings: if a single string is passed, then `dropDownList` will be filtered by this property;
 * if an array of string are passed, then `dropDownList` is filtered by keyword matches to any/all of the properties.
 * E.g. if we pass an array, `["name", "id", "userName"]` to `filterBy`, and we have pass an array of objects to `dropDownList`,
 * and we enter the keyword "foo" in the input field, the dropdown will display a list of items where ANY of the values of the object properties ["name", "id", "userName"]
 * match our keyword "foo".
 * @prop dropDownList [array}: Array of objects. We can pass objects with any properties we want, but the object should have at least one property
 * equal to at least one of the properties passed to `filterBy`. N.b. like all lists in React, the object should have an id property as well.
 * @prop onSearchListSelect {function}: The callback function when user clicks on a dropdown item.
 *
 * @example
 *  // markup:
 *  // matches Name and City properties of the list:
 *  <SearchList
 *      onSearchListSelect={(user) => this.searchOnSelect(user)}
 *      placeholder="Enter Search Term"
 *      dropDownList={this.state.myList}
 *      filterBy={["Name", "City"]}
 *      displayedProperty="Name"
 *  />
 *
 *  // state:
 *  state = {
 *      myList: [
 *          { Name: 'Arjun',        Id: '12hs3'     City: 'Mumbai' },
 *          { Name: 'Ivan',         Id: '1439a',    City: 'Volgograd' },
 *          { Name: 'Anastasia',    Id: 'h2349',    City: 'St Petersburg' },
 *          { Name: 'Ganesh',       Id: '0j2134',   City: 'Mysore' },
 *      ];
 *  };
 *
 *  // onSearchListSelect handler:
 *  searchOnSelect = (user) => {
 *      // do something:
 *      console.log("Name: ", user.Name);
 *  };
 */
const SearchList = class extends Component {
    // ---------- state: ----------
    state = {
        isActive: false,
        filterKeyword: '',
    };

    // ---------- methods: ----------
    /**
     * @method
     * @methodOf SearchList
     *
     * Handler for when user clicks on a dropdown option -- fires the `onSearchListSelect` prop and hides the dropdown.
     *
     * @param item {object}: The selected option from `dropDownList` list.
     */
    optionOnClick = (item) => {
        this.props.onSearchListSelect(item);
        this.setState({isActive: false});
    };

    /**
     * @method
     * @methodOf SearchList
     *
     * Handler for when user enter text in the input element of component -- activates dropdown and updates the `filterKeyword` state variable.
     *
     * @param val {string}: The text value of the input.
     */
    filterKeywordOnChange = (val) => {
        this.setState({
            isActive: true,
            filterKeyword: val.toLowerCase()
        });
    };

    /**
     * @method
     * @methodOf SearchList
     *
     * Handler for when user clicks outside the component -- hides the dropdown.
     *
     * This is an out-of-the-box handler provided by rect-onclickoutside <https://www.npmjs.com/package/react-onclickoutside#es6-class-component>.
     * Anytime the user clicks outside the component, the handler is called.
     */
    handleClickOutside = () => {
        this.setState({isActive: false});
    };

    /**
     * @method
     * @methodOf SearchList
     *
     * The displayed `dropDownList` (prop) filtered by user entered keywords.
     *
     * @return {array}: The filtered array of objects to be displayed in the dropdown.
     */
    displayedList = () => {
        if (!this.props.dropDownList) {
            return [];
        }

        return this.props.dropDownList
            .filter(item => {
                let shouldShowRep = false;

                if (typeof this.props.filterBy === 'string') {
                    const filterProp = item[this.props.filterBy].toLowerCase();

                    shouldShowRep = filterProp.includes(this.state.filterKeyword);

                } else if (Array.isArray(this.props.filterBy)) {

                    // loop through array of props to see keywords match:
                    for(let x of this.props.filterBy) {
                        const filterProp = item[x].toLowerCase();

                        // if any of the filterBy items match, then we show the rep:
                        if (filterProp.includes(this.state.filterKeyword)) {
                            shouldShowRep = true;
                            break;
                        }
                    }
                }

                return shouldShowRep;
            });
    };

    render() {
        return (
            <div className={css.SearchList}>

                <input className="input"
                       type="text"
                       placeholder={this.props.placeholder}
                       value={this.state.filterKeyword}
                       onChange={(event) => this.filterKeywordOnChange(event.target.value)}
                />

                <div className={this.state.isActive ? `${css.SearchList__List}` : 'd-none'}>
                    <ul>
                        {
                            this.displayedList().map(item => {
                                return (
                                    <li
                                        key={item.Id || item.id}
                                        onClick={ () => this.optionOnClick(item)}
                                    >
                                        {item[this.props.displayedProperty]}
                                    </li>
                                );
                            })
                        }
                    </ul>
                </div>
            </div>
        );
    }
};

// In order to use react-onclickoutside, we wrap the component -- see <https://www.npmjs.com/package/react-onclickoutside#usage>:
export default onClickOutside(SearchList);
